"use client";
import Link from "@/components/ui/Link";
import { useEffect, useState } from "react";
import { Menu, Search } from "lucide-react";
import { SidebarNav } from "@/components/layout/SidebarNav";
import { SITE } from "@/lib/seo/site";
import { SAME_ORIGIN_LOGO, SAME_ORIGIN_WORDMARK } from "@/components/layout/brand-assets";

/**
 * The animated mark is 64 KB from the CDN; the static mark is 9.6 KB from this
 * origin. First paint gets the static one, and the animation is swapped in
 * once the page has loaded and the main thread is idle — so on a slow mobile
 * connection it no longer competes with the page's own first paint. It plays
 * once (loop count 1) whenever it arrives, so the end state is unchanged.
 */
function useAnimatedLogoAfterLoad(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    const swap = () => {
      // Safari shipped requestIdleCallback only recently; older iOS falls back.
      const ric = window.requestIdleCallback as Window["requestIdleCallback"] | undefined;
      if (ric) {
        idleId = window.requestIdleCallback(() => setReady(true), { timeout: 3000 });
      } else {
        timeoutId = window.setTimeout(() => setReady(true), 200);
      }
    };
    if (document.readyState === "complete") swap();
    else window.addEventListener("load", swap, { once: true });
    return () => {
      window.removeEventListener("load", swap);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);
  return ready;
}

export function Header() {
  const [open, setOpen] = useState(false);
  const animatedLogo = useAnimatedLogoAfterLoad();
  return (
    <>
      <header
        data-track-zone="header"
        className="border-border sticky top-0 z-50 w-full border-b bg-white"
      >
        <div className="mx-auto flex h-16 w-full max-w-full items-center justify-between px-4">
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            className="text-ink hover:bg-muted -ml-2 inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full"
          >
            <Menu className="h-6 w-6" />
          </button>
          <Link href="/" aria-label="Sticko home" className="inline-flex items-center gap-2">
            {/* Animated brand mark. Animated WebP, converted from the GIF with
                `gif2webp -mixed -min_size -m 6`: 309KB -> 64KB for the same
                150x150 canvas and the same 1.8s of motion. It is above the
                fold in a sticky header on every page, so those 245KB were
                being paid on every uncached load.
                Plain <img>: next/image flattens an animated source to a single
                frame, and images.unoptimized is on anyway.
                - 40px to sit level with the wordmark, which renders 86x28.7:
                  its height={20} is overridden by Tailwind preflight's
                  `height: auto`, so the 3:1 SVG takes its height from the 86px
                  width. Source is 150x150, so 40px still has 3.7x to spare.
                - width/height match the rendered box so the animation reserves
                  its space up front and contributes no CLS.
                - No loading="lazy": lazy-loading an above-the-fold logo would
                  only delay the paint.
                - decoding="async" keeps the 53-frame decode off the critical
                  path — the wordmark beside it can paint without waiting.
                - fetchPriority="low" because React 19 hoists this <img> into a
                  <link rel="preload" as="image"> in <head>, and without it that
                  preload landed AHEAD of the LCP sticker thumbnail's — 64KB of
                  decorative branding taking the first slot in the network queue
                  on every page. Measured on a throttled mobile run of
                  /sticker-pack/love it was request #1, finishing at 824ms while
                  the LCP image queued behind it. Low priority keeps it eager
                  (so no paint delay, unlike lazy) but stops it competing with
                  content for bandwidth. It is a 40px mark; it must never
                  outrank the image the page is judged on.
                The file carries loop count 1, so it animates once and stops;
                that is what keeps it clear of WCAG 2.2.2, which only applies
                to motion running past five seconds. If it is ever re-exported
                as looping, it needs a pause control or a prefers-reduced-motion
                fallback. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={
                animatedLogo
                  ? `${SITE.cdn}/static-content/sticko-logo-animated.webp`
                  : SAME_ORIGIN_LOGO
              }
              alt="Sticko logo — WhatsApp sticker maker app"
              width={40}
              height={40}
              decoding="async"
              fetchPriority="low"
              className="shrink-0"
            />
            {/* Plain img: SVG vector, ~3.6KB, same-origin (see brand-assets.ts). */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={SAME_ORIGIN_WORDMARK} alt="Sticko" width={86} height={20} />
          </Link>
          <Link
            href="/search"
            aria-label="Search stickers"
            className="text-ink hover:bg-muted -mr-2 inline-flex h-10 w-10 items-center justify-center rounded-full"
          >
            <Search className="h-5 w-5" />
          </Link>
        </div>
      </header>
      <SidebarNav open={open} onClose={() => setOpen(false)} />
    </>
  );
}
